import { forwardRef, type MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { cn } from '@/app/utils';
import { useSliderDrag } from '../_hooks/use-slider-drag';
import {
  getClosestThumb,
  getPositionFromValue,
  getValueFromPosition as getValueFromPositionUtil,
  validateAndFixValues,
} from '../_utils';
import { SLIDER_STYLES, THUMB_SRC } from '../constants';
import type { MultipleSliderProps, SliderValue } from '../types';
import { SliderThumb } from './SliderThumb';

export const MultipleSlider = forwardRef<HTMLDivElement, MultipleSliderProps>(
  (
    {
      className,
      value,
      defaultValue = [0, 100],
      min = 0,
      max = 100,
      minGap = 0,
      step = 1,
      onValueChange,
      disabled = false,
      showTooltip = false,
      tooltipFormatter = (value) => value.toString(),
      tooltipSide = 'top',
      minGapToast,
    },
    ref,
  ) => {
    // 内部状态管理
    const [internalValue, setInternalValue] = useState<SliderValue>(value || defaultValue);

    // 当前值（受控 vs 非受控）
    const currentValue = useMemo(() => {
      return value !== undefined ? value : internalValue;
    }, [value, internalValue]);

    const sliderRef = useRef<HTMLDivElement>(null);

    // 更新值的函数
    const updateValue = useCallback(
      (newValue: SliderValue) => {
        const [leftValue, rightValue] = newValue;
        const validatedValue = validateAndFixValues({ leftValue, rightValue, min, max, minGap, step });

        if (validatedValue) {
          // 更新内部状态（非受控模式）
          if (value === undefined) {
            setInternalValue(validatedValue);
          }

          // 触发回调
          onValueChange?.(validatedValue);
        } else {
          if (minGapToast) {
            toast({
              message: minGapToast,
            });
          }
        }
      },
      [min, max, minGap, step, value, onValueChange, minGapToast],
    );

    // 从鼠标位置计算值
    const getValueFromPosition = useCallback(
      (clientX: number) => {
        if (!sliderRef.current) return min;
        return getValueFromPositionUtil(clientX, sliderRef.current, min, max);
      },
      [min, max],
    );

    // 使用拖拽 Hook
    const { isDragging, handleTouchStart, handleRangeTouchStart } = useSliderDrag({
      disabled,
      currentValue,
      min,
      max,
      sliderRef,
      getValueFromPosition,
      updateValue,
    });

    // 点击轨道事件
    const handleTrackClick = useCallback(
      (event: MouseEvent) => {
        if (disabled || isDragging !== null) return;

        const newValue = getValueFromPosition(event.clientX);
        const [leftValue, rightValue] = currentValue;
        const closestThumb = getClosestThumb(newValue, leftValue, rightValue);

        if (closestThumb === 0) {
          updateValue([newValue, rightValue]);
        } else {
          updateValue([leftValue, newValue]);
        }
      },
      [disabled, isDragging, currentValue, getValueFromPosition, updateValue],
    );

    // 计算滑块位置
    const leftPosition = getPositionFromValue(currentValue[0], min, max);
    const rightPosition = getPositionFromValue(currentValue[1], min, max);

    return (
      <div ref={ref} className={cn(SLIDER_STYLES.container, disabled && 'cursor-not-allowed opacity-50', className)}>
        <style>{`
        .custom-thumb {
          width: 5px;
          background-image: url(${THUMB_SRC});
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          border-radius: 5px;
        }
      `}</style>

        {/* 轨道 */}
        <div ref={sliderRef} className={SLIDER_STYLES.track} onClick={handleTrackClick}>
          {/* 轨道背景 */}
          <div className={SLIDER_STYLES.trackBackground} />

          {/* 选中范围 */}
          <div
            className={cn(
              SLIDER_STYLES.range.base,
              isDragging === 'range' && SLIDER_STYLES.range.dragging,
              disabled && SLIDER_STYLES.range.disabled,
            )}
            style={{
              left: `${leftPosition}%`,
              width: `${rightPosition - leftPosition}%`,
            }}
            onMouseDown={handleRangeTouchStart}
            onTouchStart={handleRangeTouchStart}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {/* 左滑块 */}
        <SliderThumb
          position={leftPosition}
          thumbIndex={0}
          isDragging={isDragging}
          disabled={disabled}
          value={currentValue[0]}
          showTooltip={showTooltip}
          tooltipFormatter={tooltipFormatter}
          tooltipSide={tooltipSide}
          onMouseDown={handleTouchStart}
          onTouchStart={handleTouchStart}
        />

        {/* 右滑块 */}
        <SliderThumb
          position={rightPosition}
          thumbIndex={1}
          isDragging={isDragging}
          disabled={disabled}
          value={currentValue[1]}
          showTooltip={showTooltip}
          tooltipFormatter={tooltipFormatter}
          tooltipSide={tooltipSide}
          onMouseDown={handleTouchStart}
          onTouchStart={handleTouchStart}
        />
      </div>
    );
  },
);
